import { Question, TemplateField, TemplateValue, QuestionWithFields, QuestionType } from '@shared/types/database'
import { randomId } from '@/utilities/template-helpers'
import A from '@/utilities/alpha'

// Options argument is typed only to help devs when calling the function.
export default function getQuestion (questionType: Question['questionType'], options: QuestionCreationOptions | any) {
  const validQuestionTypes = ['single-select', 'multi-select', 'matrix-likert', 'rate', 'rate-only', 'rank', 'textarea', 'description']
  if (!validQuestionTypes.includes(questionType)) throw new Error('invalid question type')

  return generateQuestion(questionType, options)(addQuestionPrompt)(addScale)(addList)()
}

function generateQuestion (questionType: Question['questionType'], options: QuestionCreationOptions | any) {
  options = supplyMissingOptions(options)
  const { questionId, custom, edited, answers, explain } = options

  const newFields = {} as Record<string, TemplateField>
  const newValues = {} as Record<string, TemplateValue>
  const newQuestion: Question = {
    id: questionId,
    label: '{{slot}}',
    custom,
    edited,
    explain,
    questionType,
    templateFields: []
  }

  // If we're editing webpage_6_feedback or a matrix, we want to pass through their "answers" field
  const cameWithAnswers = !!answers
  if (cameWithAnswers) newQuestion.answers = answers

  const mutationOptions = { newQuestion, newFields, newValues, options }

  return (callback: any) => callback(mutationOptions)
}

function supplyMissingOptions (options: any) {
  const newRandomIds = {
    questionId: randomId(),
    inputId: `${randomId()}-input`,
    minLabelId: `${randomId()}-input`,
    maxLabelId: `${randomId()}-input`,
    minRatingId: `${randomId()}-number-select`,
    maxRatingId: `${randomId()}-number-select`,
    listId: `${randomId()}-list`
  }
  // All options should be passed in when editing a question
  const defaults = options.edited ? {} : {
    minLabel: 'Not at all',
    maxLabel: 'Extremely',
    minRating: 1,
    maxRating: 5,
    question: '',
    listOptions: ['', ''],
    scale: {
      dimension: 'Custom',
      properties: {
        minLabel: 'Not at all',
        maxLabel: 'Extremely',
        minRating: 1,
        maxRating: 5
      },
      grouping: [
        {
          label: 'Extremely',
          percentage: {
            max: 1,
            min: 0.68
          }
        }, {
          label: 'Neutral',
          percentage: {
            max: 0.67,
            min: 0.33
          }
        }, {
          label: 'Not at all',
          percentage: {
            max: 0.32,
            min: 0
          }
        }]
    }
  }
  return Object.assign({}, newRandomIds, defaults, options)
}

const questionsWithList = ['single-select', 'multi-select', 'matrix-likert', 'rank']
const questionsWithScale = ['matrix-likert', 'rate', 'rate-only']

function addQuestionPrompt (mutationOptions: MutationOptions & { options: TextareaCreationOptions }): NextFunction {
  const moClone = A.clone(mutationOptions)
  const { newQuestion, newFields, newValues, options } = moClone
  const { question } = options
  const promptInputId = `${randomId()}-input`
  newFields[promptInputId] = {
    type: 'textarea',
    label: '',
    placeholder: A.stripHTML(question) || 'Question',
    id: promptInputId
  }
  newValues[promptInputId] = {
    value: question
  }
  newQuestion.templateFields.push({
    id: promptInputId,
    placement: 'inline'
  })
  return (callback) => callback ? callback(moClone) : moClone
}

function addScale (mutationOptions: MutationOptions & { options: RateCreationOptions }): NextFunction {
  const moClone = A.clone(mutationOptions)
  if (questionsWithScale.includes(mutationOptions.newQuestion.questionType)) {
    const { newQuestion, newFields, newValues, options } = moClone
    const { minLabel, maxLabel, minRating, maxRating, minRatingId, maxRatingId, minLabelId, maxLabelId, scale } = options
    newFields[minRatingId] = {
      type: 'number-select',
      options: [0, 1],
      id: minRatingId
    }
    newFields[maxRatingId] = {
      type: 'number-select',
      options: [3, 4, 5, 6, 7, 8, 9, 10],
      id: maxRatingId
    }
    newFields[minLabelId] = {
      type: 'input',
      label: '',
      placeholder: A.stripHTML(minLabel),
      id: minLabelId
    }
    newFields[maxLabelId] = {
      type: 'input',
      label: '',
      placeholder: A.stripHTML(maxLabel),
      id: maxLabelId
    }
    newValues[minLabelId] = {
      value: minLabel
    }
    newValues[maxLabelId] = {
      value: maxLabel
    }
    newValues[minRatingId] = {
      value: minRating
    }
    newValues[maxRatingId] = {
      value: maxRating
    }
    newQuestion.templateFields.push({
      id: minRatingId,
      placement: 'min-rating'
    }, {
      id: maxRatingId,
      placement: 'max-rating'
    }, {
      id: minLabelId,
      placement: 'min-label'
    }, {
      id: maxLabelId,
      placement: 'max-label'
    })
    newQuestion.scale = scale
  }
  return (callback: any) => callback ? callback(moClone) : moClone
}

function addList (mutationOptions: MutationOptions & { options: SelectCreationOptions }): NextFunction {
  const moClone = A.clone(mutationOptions)
  if (questionsWithList.includes(mutationOptions.newQuestion.questionType)) {
    const { newQuestion, newFields, newValues, options } = moClone
    const { listOptions } = options
    const listId = options.listId || `${randomId()}-list`
    newFields[listId] = {
      type: 'list',
      label: mutationOptions.newQuestion.questionType === QuestionType.MultiSelect ? 'Select all that apply:' : 'Please select one:',
      id: listId
    }
    newValues[listId] = {
      value: listOptions,
      lockOptions: false
    }
    newQuestion.templateFields.push({
      id: listId,
      placement: 'append'
    })
  }
  return (callback: any) => callback ? callback(moClone) : moClone
}
interface QuestionCreationOptions {
  custom: boolean, // If the question has been added to the template
  edited: boolean, // If the question was edited in the template (cannot be custom)
  question: string, // The plaintext question prompt
  explain: boolean,
  minLabel: string, // For rating Qs; minimum rating text
  maxLabel: string, // For rating Qs; maximum rating text
  minRating: 0 | 1, // For rating Qs; the minimum
  maxRating: 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10, // For rating questions; the maximum
  listOptions: string[], // Options in rank & single/multiselect Qs
  listId: string, // Template field id for a select/rank list
  inputId: string, // ... text input
  maxRatingId: string, // ... max rating dropdown
  minRatingId: string, // ... min rating dropdown
  minLabelId: string, // ... min rating text input
  maxLabelId: string, // ... max rating text input
  questionId: string, // A question id (i.e. user_discovery_3)
  scale: {
    dimension: string, // i.e. positive-negative
    grouping: {
      label: string, // Either min or max label
      percentage: {
        min: number, // # 0 - 1
        max: number // # 0 - 1
      }
    }[],
    properties: {
      maxLabel: string, // see above
      minLabel: string, // see above
      maxRating: number, // see above
      minRating: number, // see above
      naOption?: boolean
    }
  }
  answers?: { label: string }[] // For matrix || webpage_feedback_6. Passed through.
}

export type RateCreationOptions = Pick<QuestionCreationOptions, 'question' | 'questionId' | 'minLabel' | 'maxLabel' | 'minRating' | 'maxRating' | 'minRatingId' | 'maxRatingId' | 'minLabelId' | 'maxLabelId' | 'custom' | 'edited' | 'scale'>

export type SelectCreationOptions = Pick<QuestionCreationOptions, 'listOptions' | 'custom' | 'edited' | 'listId' | 'inputId' | 'question' | 'questionId' | 'answers'>

export type MatrixCreationOptions = Pick<QuestionCreationOptions, 'question' | 'questionId' | 'answers' | 'edited' | 'custom' | 'minLabel' | 'maxLabel' | 'minRating' | 'maxRating' | 'minRatingId' | 'maxRatingId' | 'minLabelId' | 'maxLabelId' | 'scale' | 'listId' | 'listOptions' | 'inputId'>

export type TextareaCreationOptions = Pick<QuestionCreationOptions, 'custom' | 'edited' | 'question' | 'questionId'>

interface NextFunction {
  (next: any): MutationOptions | NextFunction
}

interface MutationOptions {
  newQuestion: QuestionWithFields,
  newFields: Record<string, TemplateField>,
  newValues: Record<string, TemplateValue>,
}
